import React from 'react';
import { FaFilter, FaSearch, FaSync } from 'react-icons/fa';
import { TIME_PRESETS, RECENT_STATUS_FILTERS } from '../utils/constants';

/**
 * Pannello filtri per le modifiche recenti:
 * - mie modifiche / tutte;
 * - stato generale (tutte / da generare / generate dopo la modifica);
 * - intervallo temporale (preset + custom);
 * - ricerca testuale.
 */
export default function RecentFilters({ filters, onSetFilter, onReload, loading }) {
  const { mine, timePreset, since, until, search, genStatus } = filters;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <FaFilter className="text-primary" size={13} />
          Filtri
        </div>
        <button
          type="button"
          onClick={onReload}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-gray-700"
          aria-label="Ricarica modifiche recenti"
        >
          <FaSync className={loading ? 'animate-spin' : ''} size={12} />
          Ricarica
        </button>
      </div>

      {/* Mie modifiche / tutte */}
      <div
        role="group"
        aria-label="Ambito modifiche"
        className="mb-4 inline-flex rounded-xl bg-gray-100 p-1 dark:bg-gray-900"
      >
        {[
          { value: false, label: 'Tutte' },
          { value: true, label: 'Mie modifiche' },
        ].map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onSetFilter('mine', opt.value)}
            aria-pressed={mine === opt.value}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              mine === opt.value
                ? 'bg-white text-primary shadow-sm dark:bg-gray-700 dark:text-primary'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Stato generale della generazione */}
      <div className="mb-4">
        <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
          Stato generazione
        </span>
        <div
          role="group"
          aria-label="Filtra per stato generazione"
          className="flex flex-wrap gap-2"
        >
          {RECENT_STATUS_FILTERS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onSetFilter('genStatus', opt.id)}
              aria-pressed={(genStatus || 'all') === opt.id}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                (genStatus || 'all') === opt.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-300 text-gray-500 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ricerca testuale */}
      <div className="relative mb-4">
        <FaSearch
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={12}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => onSetFilter('search', e.target.value)}
          placeholder="Cerca per nome o tabella…"
          aria-label="Ricerca testuale macrofunzioni"
          className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Intervallo temporale */}
      <div>
        <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
          Intervallo temporale
        </span>
        <div className="flex flex-wrap gap-2">
          {TIME_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              onClick={() => onSetFilter('timePreset', preset.id)}
              aria-pressed={timePreset === preset.id}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                timePreset === preset.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-300 text-gray-500 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-400'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {timePreset === 'custom' && (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Da
              <input
                type="date"
                value={since}
                onChange={(e) => onSetFilter('since', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </label>
            <label className="text-xs text-gray-500 dark:text-gray-400">
              A
              <input
                type="date"
                value={until}
                onChange={(e) => onSetFilter('until', e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
